import { useState } from "react";
import { PaginationProps } from "antd/lib";
import { useMutation, useQuery } from "react-query";
import {
  Badge,
  Button,
  Card,
  Col,
  Divider,
  Form,
  Input,
  Row,
  Space,
  Switch,
  Tooltip,
} from "antd";
import GenericTable from "../../../components/Commons/Table/generic-table.component";
import { CloseOutlined, DeleteOutlined, EditOutlined } from "@ant-design/icons";
import ShowDataLength from "../../../components/Commons/Table/results-length-table.component";
import { getAll, remove, update } from "../../../services/categories.service";
import { FilterValue } from "antd/es/table/interface";
import { AlertService } from "../../../services/alert.service";
import GenericDrawer from "../../../components/Commons/Drawer/generic-drawer.component";
import CategoryForm from "./Form";
import ConfirmModal from "../../../components/Commons/Modal/confirm-modal.component";
import ProfilePermissionService from "../../../services/profilePermissions.service";
import { isMobile } from "react-device-detect";
import { expenseTypes } from "../../../Enums/ExpenseTypes.enums";

const { Search } = Input;

export default function CategoriesList() {
  const [categories, setCategories] = useState([]);
  const [currentPage, setCurrentPage] = useState<number | undefined>(1);
  const [perPage, setPerPage] = useState<number | undefined>(20);
  const [total, setTotal] = useState<number>(0);
  const [search, setSearch] = useState<string>("");
  const [order, setOrder] = useState<string>("name-asc");
  const [formSearch] = Form.useForm();
  const [openCategoryDrawerForm, setOpenCategoryDrawerForm] =
    useState<boolean>(false);
  const [categoryToEdit, setCategoryToEdit] = useState<any>();
  const [leaveThePage, setLeaveThePage] = useState<boolean>(false);
  const [valuesChanged, setValuesChanged] = useState<boolean>(false);
  const [removeCategory, setRemoveCategory] = useState<any | undefined>(
    undefined
  );

  const profilePermissionService = ProfilePermissionService.getInstance();

  const { isLoading: categoryLoading, refetch: loadCategories } = useQuery(
    ["loadCategories", currentPage, search, order, perPage],
    () => getAll({ page: currentPage, per_page: perPage, order, search }),
    {
      onSuccess: (response) => {
        setCategories(
          response.data.map((category: any) => {
            return {
              key: category.data.id,
              ...category.data,
            };
          })
        );

        setCurrentPage(response.meta.current_page);
        setPerPage(response.meta.per_page);
        setTotal(response.meta.total);
      },
      refetchOnWindowFocus: false,
    }
  );

  const { mutate: removeCategoryMutate } = useMutation(remove, {
    onSuccess: () => {
      AlertService.sendAlert([{ text: "Categoria apagado com sucesso." }]);

      setRemoveCategory(undefined);

      loadCategories();
    },
  });

  const onSearch = (search_value: any) => {
    setCurrentPage(1);
    setSearch(search_value);
  };

  const handleTableChange = (
    pagination: PaginationProps,
    filters: Record<string, FilterValue | null>,
    sorter: any
  ) => {
    if (pagination.current !== currentPage) {
      setCurrentPage(pagination.current);
    }

    if (pagination.pageSize !== perPage) {
      setPerPage(pagination.pageSize);
      setCurrentPage(1);
    }

    if (sorter.order) {
      let orderBy = `${sorter.field}-${sorter.order.substr(
        0,
        sorter.order.length - 3
      )}`;

      if (order !== orderBy) {
        setOrder(orderBy);
      }
    }
  };

  const onOpenCategoryDrawerForm = (record: any) => {
    setCategoryToEdit(record);
    setOpenCategoryDrawerForm(true);
  };

  const onCloseCategoryDrawerForm = () => {
    setOpenCategoryDrawerForm(false);
    setCategoryToEdit(undefined);
    setValuesChanged(false);
  };

  const onSavedForm = (successMessage: string) => {
    onCloseCategoryDrawerForm();

    setTimeout(() => {
      AlertService.sendAlert([{ text: successMessage }]);

      loadCategories();
    }, 500);
  };

  const onLeaveThePage = () => {
    if (!categoryToEdit || valuesChanged) {
      setLeaveThePage(true);
    } else {
      onCloseCategoryDrawerForm();
    }
  };

  const { mutate: updateActiveStatusMutate } = useMutation(update, {
    onSuccess: () => {
      AlertService.sendAlert([
        { text: "Status da categoria atualizado com sucesso." },
      ]);
    },
  });

  const onChangeActiveStatus = (checked: boolean, id: number) => {
    const obj = {
      values: { active: checked },
      id,
    };

    updateActiveStatusMutate(obj);
  };

  const columns: any = [
    {
      title: "Categoria",
      dataIndex: "name",
      key: "name",
      sorter: true,
      render: (_: any, record: any) => (
        <Badge color={record.color} text={record.name} />
      ),
    },
    {
      title: "Tipo",
      dataIndex: "type",
      key: "type",
      sorter: true,
      render: (_: any, record: any) =>
        record.type
          ? expenseTypes.find((type) => type.value === record.type)?.label || ""
          : "",
      responsive: ["md", "lg", "xl"],
    },
    {
      title: "Ativo?",
      dataIndex: "active",
      key: "active",
      sorter: true,
      width: 150,
      render: (_: any, record: any) => {
        return (
          <Tooltip
            title={record.active ? "Desativar categoria" : "Ativar categoria"}
          >
            <Switch
              checked={record.active}
              onChange={(event: boolean) => {
                onChangeActiveStatus(event, record.id);
                record.active = event;
              }}
            />
          </Tooltip>
        );
      },
      responsive: ["md", "lg", "xl"],
    },
    {
      title: "Ações",
      key: "action",
      render: (_: any, record: any) =>
        profilePermissionService.hasPermission(
          "expenses-categories:write"
        ) && (
          <Space size="small" direction={isMobile ? "vertical" : "horizontal"}>
            <Tooltip placement="top" title="Editar">
              <Button
                type="default"
                shape="circle"
                size="middle"
                icon={<EditOutlined />}
                onClick={() => onOpenCategoryDrawerForm(record)}
              />
            </Tooltip>

            <Tooltip placement="top" title="Apagar">
              <Button
                type="default"
                shape="circle"
                size="middle"
                icon={<DeleteOutlined />}
                onClick={() => setRemoveCategory(record)}
              />
            </Tooltip>
          </Space>
        ),
      width: "15%",
    },
  ];

  return (
    <>
      <Card
        styles={{
          body: { padding: 8 },
        }}
      >
        <Row>
          <Col
            lg={{ span: 12 }}
            md={{ span: 12 }}
            sm={{ span: 24 }}
            xs={{ span: 24 }}
          >
            <Form
              form={formSearch}
              name="search_categories"
              layout="vertical"
              autoComplete="off"
            >
              <Form.Item name="search_category">
                <Search
                  onSearch={onSearch}
                  placeholder="Pesquisa a categoria"
                  enterButton="Pesquisar"
                  allowClear
                  size="large"
                  maxLength={255}
                />
              </Form.Item>
            </Form>
          </Col>

          {profilePermissionService.hasPermission(
            "expenses-categories:write"
          ) && (
            <Col
              lg={{ span: 4, offset: 8 }}
              md={{ span: 4, offset: 8 }}
              sm={{ span: 24 }}
              xs={{ span: 24 }}
            >
              <Button
                type="primary"
                onClick={() => onOpenCategoryDrawerForm(undefined)}
                size="large"
                block
              >
                Criar nova
              </Button>
            </Col>
          )}
        </Row>

        <Divider />

        <div className="table">
          <GenericTable
            columns={columns}
            dataSource={categories}
            pagination={{
              current: currentPage,
              pageSize: perPage,
              showSizeChanger: true,
              total: total,
            }}
            loading={categoryLoading}
            totalChildren={
              <div className="zone_result_size_n_export">
                <ShowDataLength size={total} className="show_result_size" />
              </div>
            }
            empty={{
              createTheFirst: !categories.length && !search,
              createText: "Criar nova",
              textOne:
                "Esta tabela encontra-se vazia, uma vez que ainda não foram criadas categorias.",
              textTwo: profilePermissionService.hasPermission(
                "expenses-categories:write"
              )
                ? `Para começar, crie uma nova categoria.`
                : "",
              link: profilePermissionService.hasPermission(
                "expenses-categories:write"
              )
                ? () => onOpenCategoryDrawerForm(undefined)
                : "",
            }}
            handleTableChange={handleTableChange}
          />
        </div>

        <GenericDrawer
          title={!categoryToEdit ? "Criar nova categoria" : "Editar categoria"}
          children={
            <>
              <CategoryForm
                category={categoryToEdit}
                closeForm={onLeaveThePage}
                savedForm={onSavedForm}
                unavailableColors={categories.map(
                  (category: any) => category.color
                )}
                onValuesChange={() => setValuesChanged(true)}
              />

              <ConfirmModal
                open={leaveThePage}
                title="Tem certeza de que deseja sair sem gravar as alterações?"
                content="Toda a informação não gravada será perdida."
                okText="Sair"
                cancelText="Voltar"
                onOk={() => {
                  onCloseCategoryDrawerForm();
                  setLeaveThePage(false);
                }}
                onCancel={() => setLeaveThePage(false)}
              ></ConfirmModal>
            </>
          }
          onClose={onLeaveThePage}
          open={openCategoryDrawerForm}
          footerStyle={{ borderTop: "none" }}
          bodyStyle={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
          extra={
            <Button
              type="text"
              shape="circle"
              size="middle"
              icon={<CloseOutlined />}
              onClick={onLeaveThePage}
            />
          }
        />

        <ConfirmModal
          open={!!removeCategory}
          title={`Tem certeza que deseja apagar a categoria "${removeCategory?.name}"?`}
          okText="Apagar"
          cancelText="Voltar"
          onOk={() => removeCategoryMutate(removeCategory?.id)}
          onCancel={() => setRemoveCategory(undefined)}
        ></ConfirmModal>
      </Card>
    </>
  );
}
